import { MessageFilled } from '@ant-design/icons';
import { sportDontHaveImage, sportHaveDraw } from 'api/mockSportCategory';
import oddsApi from 'api/oddsApi';
import { useAppDispatch, useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import axios from 'axios';
import classNames from 'classnames';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import { showErrorModal } from 'components/Modals/CommonModals';
import { IMG_ALT } from 'consts/app';
import Typo from 'consts/typography';
import useThemes from 'hooks/useThemes';
import {
  AdditionScoreInfo,
  BetVoiceResponseModel,
  GameResponseModel,
  MatchTimeResponse,
} from 'models';
import { ScoreBoardResponse } from 'models/genius';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { BsClock } from 'react-icons/bs';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa6';
import { MdPlace } from 'react-icons/md';
import { platformStates } from 'redux/platformSlice';
import { formatDateTime, getPhaseText } from 'utils/commonUtils';
import CommentModal from './Comments/CommentsModal';
import BetModal from './Modals/BetModal';
import MoreWagger from './MoreWagger';
import OddsList from './OddsList';
import ReactionButton from './ReactionButton';
import { useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { selectMatchTimeById, updateMatchTime } from 'redux/gameSlice';
import MatchLiveInfo from './MatchLiveInfo';

interface MatchItemProps {
  category: string;
  game: GameResponseModel;
  isMoreSportsActive?: boolean;
  isPlayerPropsActive?: boolean;
  tab?: string | null;
}

const MatchItem: React.FunctionComponent<MatchItemProps> = ({
  category,
  game,
  isMoreSportsActive = false,
  isPlayerPropsActive = false,
  tab,
}) => {
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const dipatch = useAppDispatch();
  const [openMoreWager, setOpenMoreWager] = useState<boolean>(false);
  const [openMoreProps, setOpenMoreProps] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showComment, setShowComment] = useState<boolean>(false);
  const [gameBet, setGameBet] = useState<BetVoiceResponseModel | null>(null);
  const [additionData, setAdditionData] = useState<ScoreBoardResponse | null>();
  const matchTime = useSelector((state: RootState) => selectMatchTimeById(state, game.id));

  const { currentTheme } = useThemes();
  const et = useErrTranslation();

  const closeModal = () => {
    setGameBet(null);
  };

  const searchBet = async (search: string) => {
    setIsLoading(true);
    if (search && search !== '') {
      const { ok, body, error } = await oddsApi.voiceBet({
        message: search,
        gameId: game.id,
      });
      if (ok && body) {
        setGameBet(body);
      }
      if (error) {
        const onOk = () => {};
        showErrorModal({
          content:
            error.message === 'Invalid request body'
              ? et('error.validate.message.required')
              : et(error.message),
          onOk,
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
      }
    }
    setIsLoading(false);
  };

  const getAddtionalData = useCallback(async () => {
    await axios
      .get(
        `https://gsm-widgets.betstream.betgenius.com/widget-data/multisportscoreboardwidget?fixtureId=${game.id}&productName=terabet`,
      )
      .then((response) => {
        setAdditionData(response.data);
      })
      .catch(() => setAdditionData(null));
  }, [game.id]);

  let idInterval: any = -1;

  useEffect(() => {
    getAddtionalData();
  }, [getAddtionalData]);

  useEffect(() => {
    idInterval = setInterval(() => {
      if (game.status === 'LIVE') getAddtionalData();
      else clearInterval(idInterval);
    }, 3000);

    return () => {
      if (idInterval !== -1) clearInterval(idInterval);
    };
  }, [game.id, game.status]);

  const renderAddtionalInfo = () => {
    if (game.score?.additionalInfo) {
      const addtionalInfo: AdditionScoreInfo = JSON.parse(game.score?.additionalInfo);
      return `- B:${addtionalInfo.Balls} S:${addtionalInfo.Strikes} PC:${addtionalInfo.TotalPitches}`;
    }
    return '';
  };

  const getMatchTime = async () => {
    const { ok, body } = await oddsApi.getMatchTime(game.id);
    if (ok && body) {
      dipatch(updateMatchTime(body));
    }
  };

  useEffect(() => {
    if (game.status === 'LIVE') {
      getMatchTime();
    }
  }, [game.id, game.status]);

  return (
    <Fragment>
      {isLoading && (
        <div className="overlay">
          <LoadingSpinner />
        </div>
      )}
      <div className="match-item__wrapper">
        <div className="match-item items-center">
          <div className="match-item__left">
            <div className="top-team">
              <div className="flex-row items-center gap-2">
                {!sportDontHaveImage.includes(category) && (
                  <div className="team-image">
                    <img
                      onError={(e) => {
                        e.currentTarget.src = '/defaultImage.png';
                      }}
                      src={sportHaveDraw.includes(category) ? game.homeTeamLogo : game.awayTeamLogo}
                      alt={IMG_ALT}
                      className="h-6"
                    />
                  </div>
                )}
                <div className="team-name">
                  <span
                    className={`team-name__full-name primary-text-color ${
                      isPhone ? Typo.p6 : Typo.p3
                    }`}
                  >
                    {sportHaveDraw.includes(category) ? game.homeTeam : game.awayTeam}
                  </span>
                  {tab === 'MLB' && (
                    <p
                      className={`team-name__full-name second-text-color ${
                        isPhone ? Typo.p7 : Typo.p5
                      }`}
                    >
                      SP: {game.awayStarter || '--'}
                    </p>
                  )}
                </div>
              </div>
              {sportHaveDraw.includes(category) && game.status === 'LIVE' && (
                <p className={`primary-text-color m-0 ${isPhone ? Typo.p6 : Typo.p4}`}>
                  <span className="score">
                    {additionData?.multiSportScoreBoardData.matchSummaries.homeScore || ''}
                  </span>
                </p>
              )}
            </div>

            <div className="text-vs flex-row items-center ">
              <span className={`primary-text-color ${isPhone ? Typo.p4 : Typo.p2}`}>VS</span>

              <div className="match-list-btn ">
                <button onClick={() => setShowComment(true)}>
                  <MessageFilled /> {game.commentCounter}
                </button>
              </div>
              <ReactionButton game={game} />
            </div>
            <div className="bottom-team">
              <div className="flex-row items-center gap-2">
                {!sportDontHaveImage.includes(category) && (
                  <div className="team-image">
                    <img
                      onError={(e) => {
                        e.currentTarget.src = '/defaultImage.png';
                      }}
                      src={sportHaveDraw.includes(category) ? game.awayTeamLogo : game.homeTeamLogo}
                      alt={IMG_ALT}
                      className="h-6"
                    />
                  </div>
                )}
                <div className="team-name">
                  <span
                    className={`team-name__full-name primary-text-color ${
                      isPhone ? Typo.p6 : Typo.p3
                    }`}
                  >
                    {sportHaveDraw.includes(category) ? game.awayTeam : game.homeTeam}
                  </span>
                  {tab === 'MLB' && (
                    <p
                      className={`team-name__full-name second-text-color ${
                        isPhone ? Typo.p7 : Typo.p5
                      }`}
                    >
                      SP: {game.homeStarter || '--'}
                    </p>
                  )}
                </div>
              </div>
              {sportHaveDraw.includes(category) && game.status === 'LIVE' && (
                <p className={`primary-text-color m-0 ${isPhone ? Typo.p6 : Typo.p4}`}>
                  <span className="score">
                    {additionData?.multiSportScoreBoardData.matchSummaries.awayScore || ''}
                  </span>
                </p>
              )}
            </div>
          </div>
          {!sportHaveDraw.includes(category) && (
            <div className="group-scores">
              <p
                className={`${
                  game.status === 'LIVE' ? 'primary-text-color' : 'text-transparent'
                } m-0 ${isPhone ? Typo.p5 : Typo.p5}`}
              >
                <span className={`${game.status === 'LIVE' ? 'score' : ''}`}>
                  {category === 'tennis'
                    ? additionData?.multiSportScoreBoardData.matchSummaries.setAwayTeam
                    : additionData?.multiSportScoreBoardData.matchSummaries.awayScore || ''}
                </span>
              </p>
              <p
                className={`${
                  game.status === 'LIVE' ? 'primary-text-color' : 'text-transparent'
                } m-0 ${isPhone ? Typo.p5 : Typo.p5}`}
              >
                <span className={`${game.status === 'LIVE' ? 'score' : ''}`}>
                  {category === 'tennis'
                    ? additionData?.multiSportScoreBoardData.matchSummaries.setHomeTeam
                    : additionData?.multiSportScoreBoardData.matchSummaries.homeScore || ''}
                </span>
              </p>
            </div>
          )}
          <OddsList
            game={game}
            gameId={game.id}
            isMoreSportsActive={isMoreSportsActive}
            category={category}
          />
        </div>
        <div className="flex-row justify-between items-center">
          <div className="flex-row items-center match-item__action_wrapper">
            {game.status === 'LIVE' ? (
              <div className="flex-row justify-between items-center">
                <div className="match-item__footer-live">
                  <div className="dot-red"></div>
                  <span>{t('live')}</span>
                </div>
                {additionData && (
                  <div className="flex-row items-end">
                    {!!matchTime ? (
                      <MatchLiveInfo matchTime={matchTime} />
                    ) : (
                      <p
                        className={`primary-text-color m-0 pb-1 ${isPhone ? Typo.p7 : Typo.p3} ${
                          currentTheme === 'dark' ? 'neon-white' : ''
                        }`}
                      >
                        {`${getPhaseText(category, additionData)} ${renderAddtionalInfo()}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="match-item__wrapper__footer__left-side">
                  <span className={`primary-text-color ${isPhone ? Typo.p9 : Typo.p7}`}>
                    <BsClock className="mr-2" />
                    {additionData
                      ? formatDateTime(additionData?.multiSportScoreBoardData.scheduledStartTime)
                      : formatDateTime(game.startDate)}
                  </span>
                  <span className={`primary-text-color ${isPhone ? Typo.p9 : Typo.p7}`}>
                    <MdPlace className="mr-2" />{' '}
                    {additionData?.multiSportScoreBoardData.venueName || '--'}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className={classNames('flex items-center flex-row ')}>
            {/* {isPlayerPropsActive && (
              <div className="match-item__wrapper__footer__right-side">
                <button
                  onClick={() => {
                    setOpenMoreProps((prev) => !prev);
                    if (openMoreWager) {
                      setOpenMoreWager((prev) => !prev);
                    }
                  }}
                  className={`text-link cursor-pointer ${
                    isPhone ? Typo.p9 : Typo.p4
                  } main-text-color font-bold`}
                >
                  {`${t('moreProps')}`}
                  {openMoreProps ? <FaAngleUp /> : <FaAngleDown />}
                </button>
              </div>
            )} */}
            {!isMoreSportsActive && game.sport.group !== 'cricket' && (
              <div className="match-item__wrapper__footer__right-side">
                <button
                  onClick={() => {
                    setOpenMoreWager((prev) => !prev);
                    if (openMoreProps) {
                      setOpenMoreProps((prev) => !prev);
                    }
                  }}
                  className={`text-link cursor-pointer ${
                    isPhone ? Typo.p9 : Typo.p4
                  } main-text-color font-bold`}
                >
                  {`${t('moreWagers')}`}
                  {openMoreWager ? <FaAngleUp /> : <FaAngleDown />}
                </button>
              </div>
            )}
          </div>
        </div>
        {openMoreWager && (
          <Fragment>
            {sportHaveDraw.includes(category) ? (
              <OddsList
                game={game}
                category={category}
                renderWaggerSoccer={true}
                gameId={game.id}
              />
            ) : (
              <MoreWagger game={game} category={category} />
            )}
          </Fragment>
        )}
        {/* {openMoreProps && (
          <Fragment>
            <MoreProps game={game} status={status} category={category} />
          </Fragment>
        )} */}
      </div>
      <CommentModal
        title={t('comment.comments')}
        isShow={showComment}
        onClose={() => setShowComment(false)}
        gameId={game.id}
        category={category}
        homeScore={
          game.status === 'LIVE'
            ? category === 'tennis'
              ? additionData?.multiSportScoreBoardData.matchSummaries?.setHomeTeam ?? 0
              : additionData?.multiSportScoreBoardData.matchSummaries?.homeScore ?? ''
            : ''
        }
        awayScore={
          game.status === 'LIVE'
            ? category === 'tennis'
              ? additionData?.multiSportScoreBoardData.matchSummaries?.setAwayTeam ?? 0
              : additionData?.multiSportScoreBoardData.matchSummaries?.awayScore ?? ''
            : ''
        }
      />
      {gameBet && (
        <BetModal
          className="common-modal deposit-modal"
          title={t('bet.placeBet')}
          visible={true}
          closeModal={closeModal}
          bet={gameBet}
        />
      )}
    </Fragment>
  );
};

export default MatchItem;
